import { HeartPulse, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, SettingsPageShell } from "../../Components/SettingsList";
import { ProviderIcon } from "./icons";
import LoadState from "./LoadState";
import { connectionFor, isComingSoon, providerStatusLine } from "./model";
import { useConnectedHealth, useStandaloneHeader } from "./useConnectedHealth";
import "./style.css";

function ProviderRow({ provider, connection, onOpen }) {
  const soon = isComingSoon(provider);
  const line = providerStatusLine(provider, connection);
  const body = (
    <>
      <ProviderIcon provider={provider.provider} />
      <span className="ch-provider-row__text">
        <span className="ch-provider-row__name">{provider.display_name}</span>
        <span className="ch-provider-row__status">{line}</span>
      </span>
      {!soon && (
        <span className="ch-provider-row__chevron">
          <ChevronRight />
        </span>
      )}
    </>
  );
  if (soon) {
    return (
      <li className="ch-provider-row ch-provider-row--soon">
        {body}
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        className="ch-provider-row ch-provider-row--interactive"
        onClick={() => onOpen(provider.provider)}
        aria-label={`${provider.display_name}, ${line}`}
      >
        {body}
      </button>
    </li>
  );
}

/**
 * `/connected-health` — the provider list (Figma 11128:25351). Read-only on
 * web: the phone connects Apple Health / Health Connect; this screen shows
 * what is connected and opens the provider's manage screen.
 */
function ConnectedHealth() {
  const navigate = useNavigate();
  useStandaloneHeader();
  const { status, data, reload } = useConnectedHealth();
  const copy = data?.copy || {};
  const providers = data?.providers || [];

  return (
    <SettingsPageShell title={copy.screen_title || "Connected Health"} onBack={() => navigate(-1)}>
      {!data ? (
        <LoadState status={status} onRetry={reload} />
      ) : (
        <div className="ch-page">
          <header className="ch-hero">
            <span className="ch-hero__badge" aria-hidden="true">
              <HeartPulse size={36} strokeWidth={1.5} />
            </span>
            <h2 className="ch-hero__title">{copy.headline || "Connect your Health"}</h2>
            {copy.intro && <p className="ch-hero__intro">{copy.intro}</p>}
          </header>

          {providers.length === 0 ? (
            <p className="ch-state">No health providers are available right now.</p>
          ) : (
            <ul className="ch-card ch-provider-list" aria-label="Health providers">
              {providers.map((p) => (
                <ProviderRow
                  key={p.provider}
                  provider={p}
                  connection={connectionFor(data.connections, p.provider)}
                  onOpen={(key) => navigate(`/connected-health/${key}`)}
                />
              ))}
            </ul>
          )}

          {copy.privacy_note && (
            <div className="ch-note">
              <span className="ch-note__icon" aria-hidden="true">
                <Lock size={20} strokeWidth={1.5} />
              </span>
              <p>{copy.privacy_note}</p>
            </div>
          )}
        </div>
      )}
    </SettingsPageShell>
  );
}

export default ConnectedHealth;
